
// Christmas cards: a folded 5x7" card of the shopper's own artwork, in packs of 10 to 100 with
// envelopes. US only — dollars, US addresses, the en site. The inside text is typeset by the backend
// (backend/Services/ChristmasCards.cs + CardSheet.cs) in the same fonts this preview loads from
// /fonts/cards, so what the shopper sees here is what prints.
//
// ⚠️ Mirrors ChristmasCards.cs (SKUs, prices, presets, fonts, length limits, years, printable
// characters) and CardSheet.cs (sizes, line breaking, shrink to fit). Change both together.

export const CARD_SKU_PREFIX = "CARD-7X5-";

export type CardPack = { sku: string; cards: number; price: number };

export const CARD_PACKS: readonly CardPack[] = [
  { sku: "CARD-7X5-10", cards: 10, price: 45 },
  { sku: "CARD-7X5-20", cards: 20, price: 79 },
  { sku: "CARD-7X5-30", cards: 30, price: 109 },
  { sku: "CARD-7X5-50", cards: 50, price: 159 },
  { sku: "CARD-7X5-100", cards: 100, price: 239 },
];

/** The pack the editor opens on: the 30, the pack marked most popular. */
export const DEFAULT_CARD_PACK = "CARD-7X5-30";

/** The tag a pack carries on the pickers: the most popular pack and the lowest price per card. */
export const CARD_PACK_BADGES: Readonly<Record<string, "popular" | "value">> = {
  "CARD-7X5-30": "popular",
  "CARD-7X5-100": "value",
};

/** Percent a pack saves per card against the pack of 10, rounded down; 0 for the pack of 10. */
export function cardPackSaving(pack: CardPack): number {
  const atTenPrice = (CARD_PACKS[0].price / CARD_PACKS[0].cards) * pack.cards;
  return Math.floor((1 - pack.price / atTenPrice) * 100);
}

export function isCardSku(sku: string | null | undefined): boolean {
  return !!sku && sku.startsWith(CARD_SKU_PREFIX);
}

export function cardPack(sku: string): CardPack | undefined {
  return CARD_PACKS.find((p) => p.sku === sku);
}

export function cardPackName(sku: string): string {
  return `Christmas cards, pack of ${cardPack(sku)?.cards ?? ""}`.trim();
}

/** The letterings the editor offers, in the order it shows them (ChristmasCards.Fonts). */
export const CARD_FONTS = ["script", "handwritten", "serif", "formal", "vintage", "typewriter", "modern"] as const;
export type CardFont = (typeof CARD_FONTS)[number];

/** CSS font-family per card font, declared in globals.css from the same files the backend prints with. */
export const CARD_FONT_FAMILY: Record<CardFont, string> = {
  script: "'Vellum Card Script', cursive",
  handwritten: "'Vellum Card Handwritten', cursive",
  serif: "'Vellum Card Serif', Georgia, serif",
  formal: "'Vellum Card Formal', Georgia, serif",
  vintage: "'Vellum Card Vintage', cursive",
  typewriter: "'Vellum Card Typewriter', 'Courier New', monospace",
  modern: "'Vellum Card Modern', Helvetica, Arial, sans-serif",
};

export const PRESET_GREETINGS: readonly string[] = [
  "Merry Christmas and a Happy New Year",
  "Wishing you a joyful holiday season",
  "Peace, love and joy this Christmas",
  "Happy holidays from our home to yours",
  "Season's greetings and warm wishes for the new year",
  "Merry Christmas from all of us",
];

export const MAX_GREETING = 80;
export const MAX_NAMES = 60;
export const MAX_MESSAGE = 300;

export type CardText = {
  greeting: string;
  names?: string;
  year?: string;
  font: CardFont;
  message?: string;
};

export function defaultCardText(): CardText {
  return { greeting: PRESET_GREETINGS[0], names: "", year: String(new Date().getFullYear()), font: "script", message: "" };
}

/** The order-item attributes the backend reads (ChristmasCards.*Key). Empty fields are left out. */
export function cardAttributes(text: CardText): Record<string, string> {
  const attributes: Record<string, string> = { cardGreeting: text.greeting.trim(), cardFont: text.font };
  if (text.names?.trim()) attributes.cardNames = text.names.trim();
  if (text.year?.trim()) attributes.cardYear = text.year.trim();
  if (text.message?.trim()) attributes.cardMessage = text.message.trim();
  return attributes;
}

export function cardTextFromAttributes(attributes: Record<string, string> | null | undefined): CardText | null {
  if (!attributes?.cardGreeting) return null;
  const font = (CARD_FONTS as readonly string[]).includes(attributes.cardFont ?? "") ? (attributes.cardFont as CardFont) : "serif";
  return {
    greeting: attributes.cardGreeting,
    names: attributes.cardNames,
    year: attributes.cardYear,
    font,
    message: attributes.cardMessage,
  };
}

/**
 * The years a card may carry: this year and next, and in January and February last year too
 * (ChristmasCards.YearAllowed).
 */
export function cardYearAllowed(year: number, today: Date = new Date()): boolean {
  const now = today.getUTCFullYear();
  return year === now || year === now + 1 || (today.getUTCMonth() <= 1 && year === now - 1);
}

/** The first problem with the text, as a key under cards.editor.errors, or null when it prints. */
export function cardTextError(
  text: CardText,
): "greeting" | "greetingLength" | "names" | "year" | "message" | "chars" | null {
  if (!text.greeting.trim()) return "greeting";
  if (text.greeting.trim().length > MAX_GREETING) return "greetingLength";
  if ((text.names ?? "").trim().length > MAX_NAMES) return "names";
  const year = (text.year ?? "").trim();
  if (year && !(/^\d{4}$/.test(year) && cardYearAllowed(Number(year)))) return "year";
  if ((text.message ?? "").trim().length > MAX_MESSAGE) return "message";
  if ([text.greeting, text.names ?? "", text.message ?? ""].some((t) => unprintableChars(t, text.font).length > 0)) return "chars";
  return null;
}

// === Characters the fonts can print ===
//
// Each card font's character map (code points, hex ranges), read from the files in
// /fonts/cards with fontTools. Anything outside it — emoji above all — would print as an empty box,
// so the editor refuses it, as ChristmasCards.Parse does from the fonts themselves.
// ChristmasCardTests checks these lists against the fonts; regenerate them when a font changes.

const CARD_GLYPHS: Record<CardFont, string> = {
  script: "21-7e,a0-ac,ae-148,14a-17e,180-181,186-18a,18e-194,196-1a1,1a4-1a5,1a9,1ac-1b9,1c0-1c7,1c9-1ca,1cc-1f0,1f4-1f5,1f8-21b,21e-220,222-223,226-233,237,23a-23e,241-24f,251,253-254,256-257,259,25b,25f-260,263-266,268-26c,26f,272,275,27d,283,288-28c,292,294-295,298,29d,2b0,2b7,2b9-2bc,2be-2c0,2c6-2c8,2ca-2cb,2d7-2dd,2ee,300-304,306-30d,30f-313,315,31b,320,323-329,32d-332,334-336,358,35f,39b,3a9,3bb,3c0,400-45f,462-463,490-491,e3f,1d58,1d5b,1d7d,1da4,1dbb,1dc4-1dc8,1dca,1e00-1e95,1e9e,1ea0-1ef9,2000-200b,2010-2011,2013-2015,2018-201a,201c-201e,2020-2022,2026,202f-2030,2032-2033,2039-203a,2044,2052,205f,2070-2071,2074-2079,207f-2089,20a1,20a3-20a4,20a6-20a7,20a9,20ab-20ae,20b1-20b2,20b4-20b5,20b8-20ba,20bc-20bd,2116,2122,2126,2150-2154,2159-215e,2202,2206,220f,2211-2212,2215,2219-221a,221e,222b,2248,2260,2264-2265,25ca,27e8-27e9,2c60-2c66,2c6d,2c72-2c73,a726-a727,a740-a741,a789-a78d,a7a8-a7aa,a7ad-a7ae,a7b2-a7b9,a7da-a7dc,ab53,fb01-fb02",
  handwritten: "21-7e,a0-17e,18f,192,1a0-1a1,1af-1b0,1c4-1cc,1e6-1e7,1ea-1eb,1fa-21b,22a-22d,230-233,237,259,2bc,2c6-2c7,2c9,2d8-2dd,300-304,306-30c,30f,311-312,31b,323-324,326-328,32e,331,335,1e80-1e85,1e9e,1ea0-1ef9,2013-2014,2018-201a,201c-201e,2020-2022,2026,2030,2039-203a,2044,2074,20a1,20a3-20a4,20a6-20a7,20a9,20ab-20ad,20b1-20b2,20b5,20b9-20ba,20bc-20bd,2116,2122,2205,2212,2215,2219,2248,2260,2264-2265,fb01-fb02",
  serif: "21-7e,a0-148,14a-17e,18f,192,1a0-1a1,1af-1b0,1c4-1cc,1d9-1da,1e6-1e7,1ea-1eb,1fa-21b,22a-22d,230-233,237,259,2b9-2ba,2bc,2c6-2c7,2d8-2dd,300-304,306-308,30b-30c,30f,311-312,315,31b,324,326-328,32e,331,1e08-1e09,1e0c-1e0f,1e14-1e17,1e1c-1e1d,1e20-1e21,1e24-1e25,1e2a-1e2b,1e2e-1e2f,1e36-1e37,1e3a-1e3b,1e42-1e43,1e46-1e49,1e4c-1e53,1e5a-1e5b,1e5e-1e5f,1e62-1e65,1e6c-1e6f,1e78-1e7b,1e80-1e85,1e92-1e93,1e97,1e9e,1ea0-1ef9,2010,2013-2014,2018-201a,201c-201e,2020-2022,2026,2030,2032-2033,2039-203a,2044,2052,2070,2074,20a1,20a3-20a4,20a6-20a7,20a9,20ab-20ad,20b1-20b2,20b5,20b9-20ba,20bc-20bd,2116,2122,2212,2215,2219,2248,2260,2264-2265,27e8-27e9,fb00-fb04",
  formal: "21-7e,a0-b4,b6-17f,18f,192,1a0-1a1,1af-1b0,1b7,1cd-1ce,1d3-1d4,1e4-1e9,1ee-1ef,1f4-1f5,1fe-1ff,218-21b,21e-21f,237,259,292,2bb-2bc,2c6-2c7,2d8-2dd,300-304,306-30c,323,326-328,335,337-338,394,3a9,3bc,3c0,400-45f,462-463,46a-46b,490-493,496-497,49a-49b,4a2-4a3,4ae-4b1,4ba-4bb,4c9-4ca,4d8-4d9,4e8-4e9,1e80-1e85,1e9e,1ea0-1ef9,2009,2010,2013-2014,2018-201a,201c-201e,2020-2022,2026,2030,2032-2033,2039-203a,2044,2052,20ac,20b9,2105,2116,2122,212a-212b,2153-2154,2190-2194,2196-2199,2202,220f,2211-2212,221a,221e,222b,2248,2260,2264-2265,25a0-25a1,25ca,fb01-fb02",
  vintage: "21-7e,a0-17f,18f,192,1a0-1a1,1af-1b0,1b7,1cd-1dc,1e4-1eb,1ee-1ef,1f4-1f5,1fa-21b,21e-21f,237,259,292,2bb-2bc,2c6-2c7,2c9-2cb,2d8-2dd,300-304,306-30c,30f,311,31b,323-324,326-328,32e,331,335,394,3a9,3bc,3c0,400-45f,462-463,46a-46b,472-475,490-49d,4a0-4a5,4aa-4ab,4ae-4b1,4b6-4bb,4c0-4c2,4cb-4cc,4cf-4d9,4dc-4df,4e2-4e9,4ee-4f9,51a-51d,524-525,1e0c-1e0d,1e20-1e21,1e24-1e25,1e2a-1e2b,1e36-1e37,1e42-1e47,1e5a-1e5b,1e60-1e63,1e6c-1e6d,1e80-1e85,1e8e-1e8f,1e92-1e93,1e97,1e9e,1ea0-1ef9,2010,2013-2014,2018-201a,201c-201e,2020-2022,2026,2030,2032-2033,2039-203a,2044,2052,2070,2074-2079,2080-2089,20a1,20a3-20a4,20a6,20a9,20ac,20ae,20b3-20b4,20b8-20ba,20bd,2116,2120,2122,2126,212a-212b,212e,2153-2154,215b-215e,2202,2206,220f,2211-2212,2215,2219-221a,221e,222b,2248,2260,2264-2265,25ca,27e8-27e9,fb00-fb04",
  typewriter: "21-7e,a0-17e,1fc-1ff,237,2c6-2c7,2d8-2dd,312,315,326,3bc,1e80-1e85,1ef2-1ef3,2013-2014,2018-201a,201c-201e,2020-2022,2026,2030,2039-203a,2044,20ac,2122,2202,220f,2212,2248,2260,fb01-fb02",
  modern: "21-7e,a0-17e,18f,192,19d,1a0-1a1,1af-1b0,1c4-1cc,1d3,1e6-1e7,1ea-1eb,1f3,1fa-21b,22a-22d,230-233,237,259,272,2bc,2c6-2c7,2c9,2d8-2dd,300-304,306-30c,30f,311-312,31b,323-324,326-328,32e,331,335,394,3bc,3c0,1e80-1e85,1e9e,1ea0-1ef9,2013-2014,2018-201a,201c-201e,2020-2022,2026,2030,2039-203a,2044,2074,20a1,20a3-20a4,20a6-20a7,20a9,20ab-20ad,20b1-20b2,20b5,20b9-20ba,20bc-20bd,2116,2122,2126,2206,220f,2211-2212,2215,2219-221a,221e,222b,2248,2260,2264-2265,25ca,fb00-fb02",
};

const glyphRanges = new Map<CardFont, number[][]>();

function printable(codePoint: number, font: CardFont): boolean {
  let ranges = glyphRanges.get(font);
  if (!ranges) {
    ranges = CARD_GLYPHS[font].split(",").map((r) => r.split("-").map((h) => parseInt(h, 16)));
    glyphRanges.set(font, ranges);
  }
  return ranges.some(([from, to = from]) => codePoint >= from && codePoint <= to);
}

/** The characters as the reader sees them (an emoji with its modifiers is one). */
function graphemes(text: string): string[] {
  const segmenter = typeof Intl !== "undefined" && "Segmenter" in Intl ? new Intl.Segmenter("en", { granularity: "grapheme" }) : null;
  return segmenter ? Array.from(segmenter.segment(text), (s) => s.segment) : Array.from(text);
}

/** The characters of `text` that `font` cannot print, each once, in the order typed. */
export function unprintableChars(text: string, font: CardFont): string[] {
  const missing: string[] = [];
  for (const char of graphemes(text.normalize("NFC"))) {
    if (/^\s+$/.test(char) || missing.includes(char)) continue;
    if (!Array.from(char).every((c) => printable(c.codePointAt(0) ?? 0, font))) missing.push(char);
  }
  return missing;
}

// === Fitting the text inside the card ===
//
// The inside text is broken into lines and shrunk exactly as CardSheet.cs does it, so the preview
// shows the size that prints and the editor refuses what cannot fit at the smallest size. Widths
// come from the browser measuring the same font files the backend prints with.

/** Print sizes in px at 300 dpi per font (CardSheet.Sizes). */
export const CARD_SIZES: Record<CardFont, { greeting: number; names: number; message: number }> = {
  script: { greeting: 120, names: 96, message: 76 },
  handwritten: { greeting: 108, names: 86, message: 68 },
  serif: { greeting: 78, names: 62, message: 50 },
  formal: { greeting: 76, names: 60, message: 50 },
  vintage: { greeting: 78, names: 62, message: 48 },
  typewriter: { greeting: 66, names: 54, message: 44 },
  modern: { greeting: 80, names: 64, message: 52 },
};

/** Line to line distance in ems per font (CardSheet.LinePitch). */
export const CARD_LINE_PITCH: Record<CardFont, number> = {
  script: 1.44,
  handwritten: 1.38,
  serif: 1.42,
  formal: 1.53,
  vintage: 1.44,
  typewriter: 1.15,
  modern: 1.15,
};

/** A 5x7in panel in print pixels, and the clear space the text keeps from its edges. */
export const CARD_PANEL_SHORT = 1500;
export const CARD_PANEL_LONG = 2100;
export const CARD_TEXT_MARGIN = 150;
/** Text shrinks in 5% steps to fit, never below this share of its size (CardSheet.MinScale). */
export const CARD_MIN_SCALE = 0.6;
/** Space between greeting, names and year, as a share of the names size. */
export const CARD_BLOCK_GAP = 0.6;

/** Width in px of `text` set in `font` at `px`. */
export type CardMeasure = (text: string, font: CardFont, px: number) => number;

/**
 * Measures with a canvas in the card fonts; exact once the fonts have loaded (document.fonts). On the
 * server, where there is no canvas, half an em per character stands in until the page hydrates.
 */
export function canvasMeasure(): CardMeasure {
  let context: CanvasRenderingContext2D | null = null;
  return (text, font, px) => {
    if (typeof document === "undefined") return text.length * px * 0.5;
    context ??= document.createElement("canvas").getContext("2d");
    if (!context || !text) return 0;
    context.font = `${px}px ${CARD_FONT_FAMILY[font]}`;
    return context.measureText(text).width;
  };
}

function collapse(value: string | undefined): string {
  return (value ?? "").split(/\s+/).filter(Boolean).join(" ");
}

// As ChristmasCards.CollapseLines: spaces collapsed per line, at most one blank line in a row.
function collapseLines(value: string | undefined): string {
  const kept: string[] = [];
  for (const line of (value ?? "").split(/\r\n|\r|\n/).map(collapse)) {
    if (!line && (kept.length === 0 || !kept[kept.length - 1])) continue;
    kept.push(line);
  }
  while (kept.length && !kept[kept.length - 1]) kept.pop();
  return kept.join("\n");
}

/** Breaks text into lines no wider than `width`, as CardSheet.Wrap: at line breaks, between words, inside an over-long word. */
export function wrapCardText(text: string, font: CardFont, px: number, width: number, measure: CardMeasure): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split("\n")) {
    let line = "";
    for (const word of paragraph.split(" ").filter(Boolean)) {
      const joined = line ? `${line} ${word}` : word;
      if (measure(joined, font, px) <= width) {
        line = joined;
        continue;
      }
      if (line) lines.push(line);
      line = word;
      while (measure(line, font, px) > width) {
        const chars = graphemes(line);
        let lo = 1;
        let hi = chars.length;
        while (lo < hi) {
          const mid = Math.floor((lo + hi + 1) / 2);
          if (measure(chars.slice(0, mid).join(""), font, px) <= width) lo = mid;
          else hi = mid - 1;
        }
        lines.push(chars.slice(0, lo).join(""));
        line = chars.slice(lo).join("");
      }
    }
    lines.push(line);
  }
  return lines;
}

type Block = { text: string; size: number };

function greetingBlocks(text: CardText): Block[] {
  const size = CARD_SIZES[text.font];
  const blocks: Block[] = [{ text: collapse(text.greeting), size: size.greeting }];
  if (collapse(text.names)) blocks.push({ text: collapse(text.names), size: size.names });
  if (collapse(text.year)) blocks.push({ text: collapse(text.year), size: size.names * 0.75 });
  return blocks;
}

function fitScale(blocks: Block[], font: CardFont, wrap: number, maxHeight: number, gap: number, measure: CardMeasure): number | null {
  for (let step = 0; step <= 8; step++) {
    const scale = 1 - step * 0.05;
    const height =
      blocks.reduce((sum, b) => sum + wrapCardText(b.text, font, b.size * scale, wrap, measure).length * b.size * scale * CARD_LINE_PITCH[font], 0) +
      (blocks.length - 1) * gap * scale;
    if (height <= maxHeight) return scale;
  }
  return null;
}

/**
 * The scale the greeting block (with names and year) and the message print at on a card of this
 * shape (CardSheet.GreetingScale / MessageScale), null when it cannot fit even at the floor, and
 * the top of the greeting block in print px (CardSheet.DrawGreeting).
 */
export function cardScales(
  text: CardText,
  landscape: boolean,
  measure: CardMeasure,
): { greeting: number | null; message: number | null; greetingTop: number } {
  const width = landscape ? CARD_PANEL_LONG : CARD_PANEL_SHORT;
  const height = landscape ? CARD_PANEL_SHORT : CARD_PANEL_LONG;
  const wrap = width - 2 * CARD_TEXT_MARGIN;
  const blocks = greetingBlocks(text);
  const gap = CARD_SIZES[text.font].names * CARD_BLOCK_GAP;
  const greeting = fitScale(blocks, text.font, wrap, height / 2 - CARD_TEXT_MARGIN, gap, measure);
  const scale = greeting ?? CARD_MIN_SCALE;
  const stack =
    blocks.reduce((sum, b) => sum + wrapCardText(b.text, text.font, b.size * scale, wrap, measure).length * b.size * scale * CARD_LINE_PITCH[text.font], 0) +
    (blocks.length - 1) * gap * scale;
  return {
    greeting,
    message: fitScale([{ text: collapseLines(text.message), size: CARD_SIZES[text.font].message }], text.font, wrap, height - 2 * CARD_TEXT_MARGIN, 0, measure),
    // Centred on the upper third, but never closer to the fold than the text margin.
    greetingTop: Math.max(CARD_TEXT_MARGIN, height * 0.27 - stack / 2),
  };
}

/**
 * Which part of the inside does not fit at the smallest size on a portrait or a landscape card,
 * as a key under cards.editor.errors (CardSheet.Overflow).
 */
export function cardFitError(text: CardText, measure: CardMeasure): "greetingFit" | "messageFit" | null {
  for (const landscape of [false, true]) {
    const scales = cardScales(text, landscape, measure);
    if (scales.greeting === null) return "greetingFit";
    if (collapseLines(text.message) && scales.message === null) return "messageFit";
  }
  return null;
}

// === Which jobs were started for cards ===
//
// The preview API says whether a design is a card design (PreviewData.isCard), and that is what opens
// the card editor. This browser memory only covers the moments before that answer exists on this
// device: the processing screen sends a remembered card job to its preview with ?cards=1, so the
// editor is there on first paint, and PostHog files the pages of a remembered job under cards.

const CARD_JOBS_KEY = "vellum:card-jobs";

function readCardJobs(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(CARD_JOBS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

/** Marks a job as a card design on this device (see above). */
export function rememberCardJob(jobId: string): void {
  try {
    const jobs = readCardJobs().filter((id) => id !== jobId.toLowerCase());
    jobs.push(jobId.toLowerCase());
    window.localStorage.setItem(CARD_JOBS_KEY, JSON.stringify(jobs.slice(-50)));
  } catch {
    // Storage unavailable: the preview simply opens on the poster, with the card add-on below it.
  }
}

export function isCardJob(jobId: string | null | undefined): boolean {
  return !!jobId && readCardJobs().includes(jobId.toLowerCase());
}

// === The Christmas deadline ===

/**
 * Last order date for Christmas delivery: Nov 30, 23:59 Eastern (EST, UTC-5). Mirrors
 * ChristmasCards.OrderDeadlineUtc, which refuses card orders, baskets and checkouts from this moment.
 */
export const CARD_ORDER_DEADLINE = new Date("2026-12-01T04:59:00Z");
/** From this moment the cards lander and top bar count down to the deadline. */
export const CARD_COUNTDOWN_FROM = new Date("2026-11-25T05:00:00Z");

/**
 * Whether cards can still be ordered for Christmas. The pages that show cards are edge-cached, so
 * call this in the browser (useCardsOpen), never while rendering on the server.
 */
export function cardsOpen(now: number | Date = Date.now()): boolean {
  return (typeof now === "number" ? now : now.getTime()) < CARD_ORDER_DEADLINE.getTime();
}

// === Product shots for the lander gallery ===
//
// Each sample front staged as a printed card (tools/pdpgen, Nano Banana Pro, against the front art
// rendered through the production prompts), in the same warm, window-lit look as the poster PDP's
// framed-print shots. The truck photos and their licences are in cars/original/xmas/CREDITS.md. No
// card carries a licence plate. `key` names its caption (home.cardPdp.gallerySubjects).
export const CARD_SHOTS = [
  { key: "car", src: "/cards/shot-car-tree-farm.webp" },
  { key: "family", src: "/cards/shot-home-family.webp" },
  { key: "pets", src: "/cards/shot-pets-corgi-tabby-white.webp" },
  { key: "vermont", src: "/cards/shot-car-vermont-white.webp" },
  { key: "dog", src: "/cards/shot-pet-golden-white.webp" },
  { key: "street", src: "/cards/shot-main-street.webp" },
] as const;
